import { useState } from "react";
import { Bike, CreditCard, MessageCircle, X } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { RideStatus, RoutePoints } from "@/components/boy/RideStatus";
import { brl, errMsg, fmtDateTime, waLink } from "@/lib/api";
import { fmtKm } from "@/lib/boy";
import { BoyTrackButton } from "@/components/boy/BoyLive";

function RideCard({ r, busy, onPay, onCancel }) {
  const unpaid = r.status === "aguardando" && r.payment.status !== "pago";
  return (
    <div className="card-surface space-y-3 p-4" data-testid={`boy-ride-${r.number}`}>
      <div className="flex flex-wrap items-center justify-between gap-2">
        <p className="font-bold">Corrida #{r.number} <span className="ml-1 text-xs font-normal text-muted-foreground">{fmtDateTime(r.created_at)}</span></p>
        <RideStatus r={r} testId={`boy-ride-status-${r.number}`} />
      </div>
      <RoutePoints from={r.origin.label} to={r.dest.label} />
      <div className="flex items-center justify-between text-sm"><span className="text-muted-foreground">{fmtKm(r.distance_km)} km</span><span className="font-semibold">{brl(r.price)}</span></div>
      {r.customer && <p className="text-xs text-muted-foreground" data-testid={`boy-ride-customer-${r.number}`}>Cliente: <b>{r.customer.name || "sem nome"}</b> · recebe o link de acompanhamento</p>}
      {r.status === "andamento" && r.track_token && <BoyTrackButton token={r.track_token} number={r.number} testId={`boy-ride-map-${r.number}`} />}
      {r.courier && (
        <div className="flex flex-wrap items-center justify-between gap-2 rounded-lg bg-sky-500/10 p-3 text-sm" data-testid={`boy-ride-courier-${r.number}`}>
          <span className="flex items-center gap-2"><Bike className="h-4 w-4 text-sky-600" /><b>{r.courier.name}</b>{r.courier.plate && <span className="font-mono text-xs">{r.courier.plate}</span>}</span>
          <a href={waLink(r.courier.whatsapp, `Olá ${r.courier.name}, sobre a corrida #${r.number}`)} target="_blank" rel="noreferrer" className="flex items-center gap-1 font-medium text-emerald-600 hover:underline" data-testid={`boy-ride-courier-wa-${r.number}`}><MessageCircle className="h-4 w-4" />WhatsApp</a>
        </div>
      )}
      {unpaid && (
        <div className="flex gap-2">
          <Button size="sm" onClick={onPay} disabled={busy} data-testid={`boy-ride-pay-${r.number}`}><CreditCard className="mr-2 h-4 w-4" />Pagar</Button>
          <Button size="sm" variant="ghost" onClick={onCancel} disabled={busy} data-testid={`boy-ride-cancel-${r.number}`}><X className="mr-2 h-4 w-4" />Cancelar</Button>
        </div>
      )}
    </div>
  );
}

export function RideList({ bx, rides, onChange }) {
  const [busy, setBusy] = useState("");
  const pay = async (r) => {
    setBusy(r.id);
    try {
      const { data } = await bx.post(`/rides/${r.id}/pay`, { origin_url: window.location.origin });
      window.location.href = data.init_point;
    } catch (e) {
      toast.error(errMsg(e));
      setBusy("");
    }
  };
  const cancel = async (r) => {
    if (!window.confirm(`Cancelar a corrida #${r.number}?`)) return;
    setBusy(r.id);
    try {
      await bx.post(`/rides/${r.id}/cancel`);
      toast.success("Corrida cancelada");
      onChange();
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy("");
    }
  };
  return (
    <section className="space-y-3" data-testid="boy-my-rides">
      <p className="eyebrow">Minhas corridas</p>
      {!rides.length && <p className="card-surface p-6 text-center text-sm text-muted-foreground" data-testid="boy-my-rides-empty">Você ainda não pediu nenhuma entrega.</p>}
      {rides.map((r) => <RideCard key={r.id} r={r} busy={busy === r.id} onPay={() => pay(r)} onCancel={() => cancel(r)} />)}
    </section>
  );
}
